import type { SearchTabOptions, SearchTabQuery } from "./searchOptions";

interface DisplayQueryParams {
  query: SearchTabQuery;
  tmpQuery: { childAgeRange: number[] };
  options: SearchTabOptions;
  childAgeMonthLabel: (month: number, maxLabel?: boolean) => string;
}

interface DisplayOptionParams {
  query: SearchTabQuery;
  options: SearchTabOptions;
}

interface AnytimeConsultationCandidateSource {
  icd10BranchName?: string | null;
}

export function splitSearchKeywords(answerKeywords: unknown): string[] {
  if (typeof answerKeywords !== "string") return [];
  return answerKeywords.split(/[\s\u3000]+/).filter((v) => v);
}

export function buildSearchTabDisplayQuery({ query, tmpQuery, options, childAgeMonthLabel }: DisplayQueryParams): string {
  const condArray: string[] = [];

  if (query.userInterest.length > 0) {
    query.userInterest.forEach((val) => {
      const found = options.userInterest.find((o) => o.value === val);
      if (found) condArray.push(found.text);
    });
  }

  if (query.doctorId == null && (query.doctorFamilyName.length > 0 || query.doctorFirstName.length > 0)) {
    condArray.push(`回答者:${query.doctorFamilyName}${query.doctorFirstName}`);
  }

  if (query.departmentType === "pediatrician") {
    if (tmpQuery.childAgeRange[0] !== 0 || tmpQuery.childAgeRange[1] !== 35) {
      condArray.push(`${childAgeMonthLabel(tmpQuery.childAgeRange[0])}〜${childAgeMonthLabel(tmpQuery.childAgeRange[1], true)}`);
    }
    if (query.childSex.length > 0) {
      query.childSex.forEach((val) => {
        const found = options.childSex.find((o) => o.value === val);
        if (found) condArray.push(found.text);
      });
    }
  } else {
    if (query.userAgeRank.length > 0) {
      query.userAgeRank.forEach((val) => {
        const found = options.userAgeRank.find((o) => o.value === val);
        if (found) condArray.push(found.text);
      });
    }
    if (query.inPregnancy.length > 0) {
      query.inPregnancy.forEach((val) => {
        const found = options.inPregnancy.find((o) => o.value === val);
        if (found) condArray.push(found.text);
      });
    }
    if (query.weeksOfPregnancyRank.length > 0) {
      query.weeksOfPregnancyRank.forEach((val) => {
        const found = options.weeksOfPregnancyRank.find((o) => o.value === val);
        if (found) condArray.push(found.text);
      });
    }
    if (query.monthsOfPostpartumRank.length > 0) {
      query.monthsOfPostpartumRank.forEach((val) => {
        const found = options.monthsOfPostpartumRank.find((o) => o.value === val);
        if (found) condArray.push(found.text);
      });
    }
  }

  return condArray.join(", ");
}

export function buildSearchTabDisplayOption({ query, options }: DisplayOptionParams): string {
  const condArray: string[] = [];
  const foundRule = options.sortRules.find((o) => o.value === query.sortRules);
  if (foundRule) condArray.push(foundRule.text);
  if (query.displayScore) condArray.push("スコア表示あり");
  return condArray.join(", ");
}

export function buildTopIcd10Candidates(anytimeConsultations: AnytimeConsultationCandidateSource[]): Array<{ name: string; score: number }> {
  if (!Array.isArray(anytimeConsultations) || anytimeConsultations.length === 0) return [];

  const rankObj: Record<string, number> = {};
  let score = 30;
  anytimeConsultations
    .map((c) => c.icd10BranchName)
    .filter(Boolean)
    .slice(0, 30)
    .forEach((name) => {
      const key = name as string;
      if (!rankObj[key]) rankObj[key] = 0;
      rankObj[key] += score;
      score -= 1;
    });

  return Object.keys(rankObj)
    .map((k) => ({ name: k, score: rankObj[k] }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
}
